import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { PreautorisationSelectors } from '../../state/preautorisation.selectors';
import { TypeProjet } from '../../../../shared/constants/type-projet.enum';
import {
  MIN_MISE_DE_FONDS_PERCENTAGE,
  MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME,
  MAX_MISE_DE_FONDS_PERCENTAGE,
  MISE_DE_FONDS_DEUXIEME_PALIER_PERCENTAGE,
  MISE_DE_FONDS_PREMIER_PALIER_PERCENTAGE,
  MISE_DE_FONDS_PREMIER_PALIER_MONTANT
} from '../../../../shared/modeles/variables';

@Injectable({
  providedIn: 'root'
})
export class MiseDeFondsService {
  private readonly PERCENTAGE_PRECISION = 0.01;

  constructor(private readonly _store: Store) {}

  calculateMiseDeFondsAmountFromPercentage(rawPercentage: any, montantPropriete: number): number {
    const miseDeFondsPercentage = this.cleanPercentageValue(rawPercentage);
    return Math.ceil((miseDeFondsPercentage / 100) * montantPropriete);
  }

  calculateMiseDeFondsPercentageFromAmount(miseDeFondsAmount: number, montantPropriete: number): number {
    let percentageResult = 0;
    if (miseDeFondsAmount && montantPropriete) {
      const rawPercentage = (miseDeFondsAmount / montantPropriete) * 100;
      percentageResult = this.cleanPercentageValue(rawPercentage);
    }
    return percentageResult;
  }

  cleanPercentageValue(rawPercentage: any): number {
    let cleanedPercentage = 0;
    rawPercentage = Number(rawPercentage);
    if (!isNaN(rawPercentage)) {
      // Don't round up when the percentage is near 5% or 20%
      // to prevent misleading SCHL premium message
      if (
        rawPercentage >= MIN_MISE_DE_FONDS_PERCENTAGE - this.PERCENTAGE_PRECISION &&
        rawPercentage < MIN_MISE_DE_FONDS_PERCENTAGE
      ) {
        cleanedPercentage = MIN_MISE_DE_FONDS_PERCENTAGE - this.PERCENTAGE_PRECISION;
      } else if (
        rawPercentage >= MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME - this.PERCENTAGE_PRECISION &&
        rawPercentage < MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME
      ) {
        cleanedPercentage = MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME - this.PERCENTAGE_PRECISION;
      } else {
        cleanedPercentage =
          Math.round(rawPercentage) !== rawPercentage ? Number(rawPercentage.toFixed(2)) : rawPercentage;
      }
    }
    return cleanedPercentage;
  }

  isMiseDeFondsValide(miseDeFondsAmount: number, montantPropriete: any): boolean {
    return (
      miseDeFondsAmount >= (miseDeFondsAmount * MIN_MISE_DE_FONDS_PERCENTAGE) / 100 &&
      miseDeFondsAmount <= (montantPropriete * MAX_MISE_DE_FONDS_PERCENTAGE) / 100
    );
  }

  determinerMiseDeFondMinimale(
    montantPropriete: number = this._store.selectSnapshot(PreautorisationSelectors.prixPropriete)
  ): number {
    if (this._store.selectSnapshot(PreautorisationSelectors.typePreautorisation) === TypeProjet.ExperimenteSecondaire) {
      if (this._store.selectSnapshot(PreautorisationSelectors.indHabitableHiver) === true) {
        return this.getMiseDeFondMinimale(montantPropriete);
      } else return this.getMiseDeFondMinimaleSansPrime(montantPropriete);
    } else if (this._store.selectSnapshot(PreautorisationSelectors.nbrLogements) > 2) {
      return this.getMiseDeFondMinimale3LogementsEtPlus(montantPropriete);
    } else {
      return this.getMiseDeFondMinimale(montantPropriete);
    }
  }

  getMiseDeFondMinimale(montantPropriete: number): number {
    return (
      (Math.min(montantPropriete, MISE_DE_FONDS_PREMIER_PALIER_MONTANT) * MISE_DE_FONDS_PREMIER_PALIER_PERCENTAGE +
        Math.max(montantPropriete - MISE_DE_FONDS_PREMIER_PALIER_MONTANT, 0) *
          MISE_DE_FONDS_DEUXIEME_PALIER_PERCENTAGE) /
      100
    );
  }

  getMiseDeFondMaximale(montantPropriete: number): number {
    return Math.ceil((montantPropriete * MAX_MISE_DE_FONDS_PERCENTAGE) / 100);
  }

  getMiseDeFondMinimaleSansPrime(montantPropriete: number): number {
    return (montantPropriete * MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME) / 100;
  }

  getMiseDeFondMinimale3LogementsEtPlus(montantPropriete: number): number {
    return (montantPropriete * MISE_DE_FONDS_DEUXIEME_PALIER_PERCENTAGE) / 100;
  }

  isPrimeSCHLApplicable(miseDeFondsAmount: number, montantPropriete: number): boolean {
    return miseDeFondsAmount < (montantPropriete * MIN_MISE_DE_FONDS_PERCENTAGE_SANS_PRIME) / 100;
  }

  isMiseDeFondsValid(
    miseDeFonds: number = this._store.selectSnapshot(PreautorisationSelectors.miseDeFonds),
    prixPropriete: number = this._store.selectSnapshot(PreautorisationSelectors.prixPropriete)
  ): boolean {
    return (
      miseDeFonds >= this.determinerMiseDeFondMinimale(prixPropriete) &&
      miseDeFonds <= this.getMiseDeFondMaximale(prixPropriete)
    );
  }
}
